import {
  afterNextRender,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { ModalBaseComponent } from '../../../shared/components/modal-base/modal-base.component';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
  take,
} from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromStore from '../../../core/store/reducers';
import { BookingsService } from '../../../core/services/bookings.service';
import { Booking } from '../../../core/models/entities/booking';
import { BookableBy } from '../../../core/models/common/bookable-by';
import { formatISO, isValid, parseISO } from 'date-fns';
import {
  ModalInstance,
  ModalService,
} from '../../../core/services/modal.service';
import { ToastService } from '../../../core/services/toast.service';
import { RecurrenceFormComponent } from '../../../shared/components/recurrence-form/recurrence-form.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { UpdateRecurrenceType } from '../../../shared/components/update-recurrence-modal/update-recurrence-modal.component';
import { BookingInvite } from '../../../core/models/entities/booking-invite';
import { TabLink } from '../../../shared/components/tabs.component';
import {
  UserOrEmail,
  UsersAutocompleteListComponent,
} from '../../../shared-manager/components/users-autocomplete-list/users-autocomplete-list.component';
import { bookingsActions } from '../../../core/store/bookings';
import { BookingsScope } from '../../../core/store/bookings/bookings.state';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DatetimeHelper } from '../../../core/helpers/datetime.helper';
import { ApiError } from '../../../core/models/common/api-error';
import { PrivateBookingDateSubformComponent } from '../private-booking-date-subform/private-booking-date-subform.component';
import { InputOption } from '../../../core/models/common/input-option';
import {
  AppLocale,
  getEditableLocales,
} from '../../../core/services/locale.service';

export interface PrivateBookingInitialData {
  roomId?: number;
  startDate?: Date;
  endDate?: Date;
}

@Component({
  selector: 'app-private-booking-main-form',
  animations: [fadeAnimation],
  template: `
    <app-modal [modalInstance]="modalInstance" (onOk)="onSubmit()">
      <div
        class="sticky -top-6 z-10 -mx-4 -mt-6 bg-white px-4 pt-6 sm:-mx-6 sm:px-6"
      >
        <app-tabs
          [links]="tabs()"
          [useRouter]="false"
          [(activeTab)]="currentTab"
        />
      </div>

      <!-- Onglet Événement -->
      <div [class.hidden]="currentTab() !== ('bookings.private-form.tab-event' | transloco)">
        @if (form) {
          <form (submit)="$event.preventDefault()">
            <app-spacer size="6"></app-spacer>
            <!-- Champs inline compacts -->
            <div class="mb-4 flex items-baseline gap-4">
              <label for="name" class="w-32 shrink-0 text-sm font-medium"
                >{{ 'bookings.private-form.label-name' | transloco }}</label
              >
              <app-text-input
                class="grow"
                [control]="form.controls.name"
                name="name"
                [placeholder]="'bookings.private-form.placeholder-name' | transloco"
                [marginBottom]="false"
                [marginTop]="false"
                size="small"
                [showValidationErrorsAsText]="false"
              />
            </div>
            <div class="mb-4 flex items-baseline gap-4">
              <label for="room_id" class="w-32 shrink-0 text-sm font-medium"
                >{{ 'bookings.private-form.label-room' | transloco }}</label
              >
              <app-select-input
                class="grow"
                [control]="form.controls.room_id"
                name="room_id"
                [options]="roomsOptions$ | async"
                [placeholder]="'bookings.private-form.placeholder-room' | transloco"
                [marginBottom]="false"
                [showValidationErrorsAsText]="false"
                size="small"
              />
            </div>

            @if (localeOptions().length > 1) {
              <div class="mb-4 flex items-baseline gap-4">
                <label for="locale" class="w-32 shrink-0 text-sm font-medium"
                  >{{ 'bookings.private-form.label-locale' | transloco }}</label
                >
                <app-select-input
                  class="grow"
                  [control]="form.controls.locale"
                  name="locale"
                  [options]="localeOptions()"
                  [hidePleaseSelect]="true"
                  [marginBottom]="false"
                  [showValidationErrorsAsText]="false"
                  size="small"
                />
              </div>
            }

            <app-private-booking-date-subform
              [room]="selectedRoomSignal()"
              [booking]="booking"
              [disabled]="!selectedRoomSignal()"
              [initialStartDate]="initialData?.startDate"
              [initialEndDate]="initialData?.endDate"
            />
          </form>
        }

        <!-- Récurrence compact : masquée si modification d'un seul événement -->
        @if (!isSingleEventScope) {
          <div class="mb-4 flex items-baseline gap-4">
            <label class="w-32 shrink-0 text-sm font-medium">{{ 'bookings.private-form.label-recurrence' | transloco }}</label>
            @if (!hasRecurrence()) {
              <span
                class="inline-block"
                [tooltip]="
                  canAddRecurrence()
                    ? ''
                    : ('bookings.private-form.tooltip-recurrence-disabled' | transloco)
                "
              >
                <button
                  type="button"
                  [disabled]="!canAddRecurrence()"
                  (click)="hasRecurrence.set(true)"
                  class="text-sm"
                  [ngClass]="
                    canAddRecurrence()
                      ? 'text-primary-600 hover:text-primary-800'
                      : 'cursor-not-allowed text-gray-400 opacity-50'
                  "
                  data-cy="add-recurrence-button"
                >
                  <i class="far fa-plus mr-1"></i>{{ 'bookings.private-form.button-add-recurrence' | transloco }}
                </button>
              </span>
            } @else {
              <button
                type="button"
                (click)="hasRecurrence.set(false)"
                class="text-sm text-red-600 hover:text-red-800"
                data-cy="delete-recurrence-button"
              >
                <i class="far fa-times mr-1"></i>{{ 'bookings.private-form.button-remove-recurrence' | transloco }}
              </button>
            }
          </div>
          @if (hasRecurrence() && hasValidDates()) {
            <div class="mb-4 rounded-lg bg-gray-100 p-3">
              <app-recurrence-form
                #recurrenceForm
                layout="inline"
                [recurrence]="booking?.recurrence"
                [bookingStart]="formatISO(startDatetime)"
                [bookingEnd]="formatISO(endDatetime)"
                [bookingId]="booking?.id"
                [roomId]="selectedRoomSignal()?.id"
                [allDay]="dateSubform()?.allDay()"
              ></app-recurrence-form>
            </div>
          }
        }

        @if ($any(form.get('custom_fields')).length > 0) {
          <hr class="my-4 border-gray-200" />
        }
        <app-custom-fields-inputs
          [visibility]="BookableBy.Private"
          [room]="selectedRoomSignal()"
          [formArray]="$any(form.get('custom_fields'))"
          [customFieldsToEdit]="booking?.custom_fields ?? null"
          [editingBookingId]="booking?.id ?? null"
          layout="inline"
          size="small"
        ></app-custom-fields-inputs>
        @if ($any(form.get('custom_fields')).length > 0) {
          <hr class="my-4 border-gray-200" />
        }

        <!-- Note interne -->
        <div class="mt-4">
          <app-rich-text-input
            [control]="form.get('comment')"
            name="comment"
            [placeholder]="'bookings.private-form.placeholder-internal-note' | transloco"
            [helpText]="'bookings.private-form.helptext-internal-note' | transloco"
            [marginBottom]="false"
          ></app-rich-text-input>
        </div>
      </div>

      <!-- Onglet Invités -->
      <div [class.hidden]="currentTab() !== ('bookings.private-form.tab-guests' | transloco)">
        <app-users-autocomplete-list
          [usersOrEmails$]="invites$"
          (deleteUserOrEmail)="deleteInvite($event)"
          (validCountChanged)="pendingInvitesCount.set($event)"
        ></app-users-autocomplete-list>
        <app-spacer size="2"></app-spacer>
        <app-textarea-input
          [control]="form.get('invites_comment')"
          name="comment"
          [label]="'bookings.private-form.label-invite-message' | transloco"
        ></app-textarea-input>
        <app-single-checkbox-input
          [label]="'bookings.private-form.label-notify-guests' | transloco"
          [control]="form.get('notify_invites')"
          name="notify_invites"
          [marginBottom]="false"
        ></app-single-checkbox-input>
      </div>
    </app-modal>
  `,
  standalone: false,
})
export class PrivateBookingMainFormComponent
  extends ModalBaseComponent
  implements OnInit
{
  bookingSaved = output<Date>();
  booking: Booking;
  protected readonly BookableBy = BookableBy;

  private _initialData: PrivateBookingInitialData;
  get initialData(): PrivateBookingInitialData {
    return this._initialData;
  }
  set initialData(value: PrivateBookingInitialData) {
    this._initialData = value;
    if (value?.roomId && !this.form.controls.room_id.value) {
      this.form.controls.room_id.patchValue(value.roomId);
    }
  }

  protected dateSubform = viewChild(PrivateBookingDateSubformComponent);
  protected recurrenceForm = viewChild(RecurrenceFormComponent);
  protected invitesFormComponent = viewChild(UsersAutocompleteListComponent);

  protected vendor = this.store.selectSignal(fromStore.selectAuthVendor);

  private rooms$ = this.store.select(
    fromStore.selectAllRoomsBookableBy(BookableBy.Private),
  );

  protected roomsOptions$ = this.rooms$.pipe(
    map((rooms) => rooms.map((room) => ({ label: room.name, value: room.id }))),
  );

  protected readonly formatISO = formatISO;
  protected sendingData: boolean;
  protected form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    room_id: new FormControl(null, [Validators.required]),
    comment: new FormControl(null),
    invites_comment: new FormControl(null),
    notify_invites: new FormControl(false),
    // Langue des e-mails envoyés au client et aux invités de cette réservation.
    locale: new FormControl<AppLocale | null>(null),
    custom_fields: new FormArray([]),
  });

  /**
   * Langues proposées par l'organisation. Quand elle n'en propose qu'une, le
   * sélecteur est masqué et cette langue s'applique d'office.
   */
  protected localeOptions = computed<InputOption[]>(() =>
    getEditableLocales(this.vendor()).map((locale) => ({
      value: locale,
      label: this.translocoService.translate(`locale.${locale}`),
    })),
  );

  // Observables RxJS basés sur le formulaire
  protected roomId$ = this.form.controls.room_id.valueChanges.pipe(
    startWith(this.form.controls.room_id.value),
    distinctUntilChanged(),
  );

  protected selectedRoom$ = combineLatest([this.rooms$, this.roomId$]).pipe(
    map(([rooms, roomId]) => rooms.find((room) => room.id == roomId) ?? null),
    distinctUntilChanged(),
  );

  // Signal pour les méthodes synchrones (recurrence form, getFormData)
  protected selectedRoomSignal = toSignal(this.selectedRoom$);

  protected hasRecurrence = signal(false);
  protected hasValidDates = computed(() => {
    const subform = this.dateSubform();
    if (!subform) return false;
    return subform.hasDate() && !subform.loadingAvailabilities();
  });
  protected canAddRecurrence = computed(
    () => this.dateSubform()?.hasDate() ?? false,
  );
  updateRecurrenceType: UpdateRecurrenceType | null = null;
  protected get isSingleEventScope(): boolean {
    return this.updateRecurrenceType === UpdateRecurrenceType.SingleEvent;
  }

  protected bookingInvites = signal<BookingInvite[]>([]);
  protected pendingInvitesCount = signal(0);
  private translocoService = inject(TranslocoService);

  protected currentTab = signal('');
  protected tabs = computed<TabLink[]>(() => {
    const total = this.bookingInvites().length + this.pendingInvitesCount();
    return [
      { label: this.translocoService.translate('bookings.private-form.tab-event') },
      {
        label: this.translocoService.translate('bookings.private-form.tab-guests'),
        badge: total > 0 ? total : undefined,
        badgeVariant: 'secondary',
      },
    ];
  });

  private elementRef = inject(ElementRef);

  constructor(
    private bookingsService: BookingsService,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
    private store: Store,
    private modalService: ModalService,
  ) {
    super();
    this.setupFocusNameOnOpen();
    this.setupAutoNotifyInvites();
  }

  private setupAutoNotifyInvites() {
    effect(() => {
      if (this.pendingInvitesCount() > 0) {
        this.form.controls.notify_invites.patchValue(true);
      }
    });
  }

  private setupFocusNameOnOpen() {
    afterNextRender(() => {
      if (this.booking || window.innerWidth < 1024) return;
      const input =
        this.elementRef.nativeElement.querySelector('input[name="name"]');
      input?.focus();
    });
  }

  ngOnInit() {
    this.currentTab.set(this.translocoService.translate('bookings.private-form.tab-event'));

    this.form.patchValue({
      name: this.booking?.name ?? '',
      room_id: this.booking?.room_id ?? this.initialData?.roomId ?? null,
      comment: this.booking?.comment ?? null,
      invites_comment: this.booking?.invites_comment ?? null,
      notify_invites: this.booking?.notify_invites ?? false,
      locale:
        (this.booking?.locale as AppLocale) ??
        getEditableLocales(this.vendor())[0],
    });

    this.hasRecurrence.set(!!this.booking?.recurrence);
    this.bookingInvites.set(this.booking?.booking_invites ?? []);
    this.modalInstance.config.update((config) => ({
      ...config,
      size: 'lg',
      alignTop: true,
    }));

    this.autoSelectSingleRoomIfNeeded();
  }

  private autoSelectSingleRoomIfNeeded() {
    const roomsFilter = this.store.selectSignal(
      fromStore.selectBookingsRoomsFilter(BookingsScope.All),
    );
    const filterIds = roomsFilter()?.ids;
    if (filterIds?.length === 1 && !this.form.controls.room_id.value) {
      this.form.controls.room_id.patchValue(filterIds[0]);
    }
  }

  invites$ = toObservable(this.bookingInvites);

  openIgnoreConflictsConfirmationModal(dates: string[]) {
    let message =
      `<div>${this.translocoService.translate('bookings.private-form.conflict-intro')}<br/> ` +
      '<ul class="mb-4">';
    for (const date of dates) {
      message += `<li>${date}</li>`;
    }
    message +=
      `</ul>${this.translocoService.translate('bookings.private-form.conflict-skip-notice')}` +
      `<div class="font-medium mt-4">${this.translocoService.translate('bookings.private-form.conflict-confirm')}</div></div>`;

    const instance = this.modalService.open(ConfirmModalComponent, {
      title: this.translocoService.translate('bookings.private-form.conflict-modal-title'),
      message,
      okAction: {
        title: this.translocoService.translate('common.button.yes'),
      },
      cancelAction: {
        title: this.translocoService.translate('common.button.no'),
      },
    });
    instance.okClicked$.subscribe(() => {
      this.onSubmit(true);
      this.modalService.close();
    });
  }

  deleteInvite(userOrEmail: UserOrEmail) {
    const modalInstance: ModalInstance = this.modalService.open(
      ConfirmModalComponent,
      {
        title: this.translocoService.translate('bookings.private-form.delete-invite-title'),
        message: this.translocoService.translate('bookings.private-form.delete-invite-confirm', { email: userOrEmail.email }),
        okAction: {
          title: this.translocoService.translate('common.button.yes'),
        },
        cancelAction: {
          title: this.translocoService.translate('common.button.no'),
        },
      },
    );
    modalInstance.okClicked$.subscribe(async () => {
      modalInstance.saving = true;
      try {
        const invite = this.booking.booking_invites.find(
          (invite) => invite.email === userOrEmail.email,
        );
        const booking = await this.bookingsService.deleteInvite(
          this.booking.id,
          invite.id,
        );
        this.bookingInvites.set(booking.booking_invites ?? []);
        this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
        this.store.dispatch(
          bookingsActions.deleteInvite({
            scope: BookingsScope.All,
            booking: this.booking,
            invite,
          }),
        );
        this.modalService.close();
        modalInstance.saving = false;
      } catch (exception: any) {
        this.toastService.error(exception.message);
        modalInstance.saving = false;
      }
    });
  }

  preselectRoom(roomId: number) {
    this.form.controls.room_id.patchValue(roomId);
  }

  get startDatetime(): Date {
    const date = this.dateSubform()?.getFormData()?.start;
    return date && isValid(date) ? date : new Date();
  }

  get endDatetime(): Date {
    const date = this.dateSubform()?.getFormData()?.end;
    return date && isValid(date) ? date : new Date();
  }

  async onSubmit(ignore_recurrence_conflicts = null) {

    if (!this.formValidationService.validate(this.form)) {
      return;
    }

    if (!this.dateSubform()?.validate()) {
      return;
    }

    if (
      this.hasRecurrence() &&
      !this.isSingleEventScope &&
      !this.formValidationService.validate(this.recurrenceForm()?.form)
    ) {
      return;
    }

    const knownDateExceptions = this.recurrenceForm()?.dateExceptions;
    if (
      this.hasRecurrence() &&
      !this.isSingleEventScope &&
      !ignore_recurrence_conflicts &&
      knownDateExceptions?.length > 0
    ) {
      this.openIgnoreConflictsConfirmationModal(knownDateExceptions);
      return;
    }

    try {
      this.modalInstance.saving = true;
      const dateFormData = this.dateSubform().getFormData();

      const data = {
        id: this.booking?.id,
        name: this.form.value.name,
        comment: this.form.value.comment,
        dates: [
          {
            start: DatetimeHelper.dateToApiFormat(dateFormData.start, true),
            end: DatetimeHelper.dateToApiFormat(dateFormData.end, true),
            all_day: dateFormData.all_day,
          },
        ],
        room_id: this.form.value.room_id,
        is_public: false,
        recurrence: this.isSingleEventScope
          ? this.booking?.recurrence
            ? {
                ...this.booking.recurrence,
                end: DatetimeHelper.dateToApiFormat(parseISO(this.booking.recurrence.end), true),
              }
            : null
          : this.hasRecurrence() ? this.recurrenceForm()?.value : null,
        ignore_recurrence_conflicts,
        update_recurrence_type: this.updateRecurrenceType,
        booking_invites: this.invitesFormComponent().validResults,
        invites_comment: this.form.value.invites_comment,
        notify_invites: this.form.value.notify_invites,
        locale: this.form.value.locale,
        custom_fields: this.form.value.custom_fields ?? null,
      };

      let booking: Booking;
      if (this.booking) {
        booking = await this.bookingsService.update(
          this.booking.id,
          // @ts-ignore
          data,
        );
      } else {
        booking = await this.bookingsService.storePrivate(data);
      }
      this.toastService.success(
        this.translocoService.translate(
          this.booking ? 'bookings.private-form.toast-updated' : 'bookings.private-form.toast-created',
          { name: booking.name },
        ),
      );
      this.store.dispatch(bookingsActions.setFocusedBooking({ booking }));
      this.store.dispatch(
        bookingsActions.upsertBooking({
          booking,
          scope: BookingsScope.All,
        }),
      );

      this.bookingSaved.emit(dateFormData.start);
      this.modalService.close();
    } catch (exception) {
      console.error(exception);
      if ((exception as ApiError).errors?.['dates_conflicts']) {
        this.openIgnoreConflictsConfirmationModal(
          (exception as ApiError).errors['dates_conflicts'],
        );
      } else {
        this.formValidationService.displayApiValidationErrors(
          this.form,
          exception as ApiError,
        );
      }
    } finally {
      this.modalInstance.saving = false;
    }
  }
}
